<template>
  <div class="schema-constructor">
    <!-- Toolbar -->
    <v-toolbar flat dense class="mb-2">
      <v-btn small text color="primary" @click="addField">
        <v-icon small left>mdi-plus</v-icon>
        Add Field
      </v-btn>
      <v-btn v-if="isRoot" small text @click="showFieldsets = !showFieldsets">
        <v-icon small left>mdi-tab</v-icon>
        Fieldsets
        <v-icon small right>{{ showFieldsets ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </v-btn>
      <v-btn-toggle
        v-if="isRoot && fieldsets.length > 0"
        v-model="viewMode"
        mandatory
        dense
        class="ml-3"
      >
        <v-btn x-small :value="'flat'">
          <v-icon x-small left>mdi-format-list-bulleted</v-icon>
          Flat
        </v-btn>
        <v-btn x-small :value="'grouped'">
          <v-icon x-small left>mdi-format-list-group</v-icon>
          Grouped
        </v-btn>
      </v-btn-toggle>
      <v-spacer />
      <v-btn small text @click="showPreviewForm = true">
        <v-icon small left>mdi-eye</v-icon>
        Preview
      </v-btn>
      <v-btn small text @click="showPreviewJson = true">
        <v-icon small left>mdi-code-json</v-icon>
        JSON
      </v-btn>
      <v-btn small text @click="showImport = !showImport">
        <v-icon small left>mdi-import</v-icon>
        Import
      </v-btn>
    </v-toolbar>

    <!-- Import JSON -->
    <v-card v-if="showImport" flat outlined class="mb-3 pa-3">
      <v-textarea
        v-model="importJson"
        label="Paste schema JSON"
        rows="6"
        dense
        outlined
        hide-details
        style="font-family: monospace; font-size: 13px;"
      />
      <div class="d-flex mt-2">
        <v-spacer />
        <v-btn small text @click="showImport = false">Cancel</v-btn>
        <v-btn small text color="primary" @click="doImport">Apply</v-btn>
      </div>
    </v-card>

    <!-- Fieldsets editor (root only) -->
    <schema-fieldset-editor
      v-if="isRoot && showFieldsets"
      :fieldsets="fieldsets"
      :fields="currentFields"
      @update="onFieldsetsUpdate"
    />

    <!-- Validation errors -->
    <v-alert v-if="displayErrors.length > 0" type="error" dense text class="mb-3">
      <div v-for="(err, i) in displayErrors" :key="i" class="text-body-2">{{ err }}</div>
    </v-alert>

    <!-- Validation warnings -->
    <v-alert v-if="displayWarnings.length > 0" type="warning" dense text class="mb-3">
      <div v-for="(w, i) in displayWarnings" :key="i" class="text-body-2">{{ w }}</div>
    </v-alert>

    <!-- Breadcrumb -->
    <schema-nested-breadcrumb
      :stack="nestedStack"
      @navigate="navigateToLevel"
    />

    <!-- Field list: flat mode -->
    <schema-field-list
      v-if="viewMode === 'flat' || !isRoot"
      :fields="currentFields"
      :fieldsets="isRoot ? fieldsets : []"
      :cms-types="contentTypes"
      :fragment-types="fragmentTypes"
      :is-top-level="isRoot"
      @update="onFieldsUpdate"
      @edit-nested="onEditNested"
    />

    <!-- Field list: grouped mode -->
    <schema-field-list-grouped
      v-else
      :fields="currentFields"
      :fieldsets="fieldsets"
      :cms-types="contentTypes"
      :fragment-types="fragmentTypes"
      :is-top-level="isRoot"
      @update="onFieldsUpdate"
      @edit-nested="onEditNested"
      @add-field="addField"
    />

    <!-- Preview dialogs -->
    <schema-preview-json
      v-model="showPreviewJson"
      :schema="outputSchema"
    />
    <schema-preview-form
      v-model="showPreviewForm"
      :schema="outputSchema"
      :locales="locales"
      :all-types="allTypes"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { API } from '@/utils/api'
import { createDefaultField } from './schemaConstants'
import { validateSchema, buildSchemaJson, makeUid } from './schemaUtils'
import SchemaFieldsetEditor from './SchemaFieldsetEditor'
import SchemaNestedBreadcrumb from './SchemaNestedBreadcrumb'
import SchemaFieldList from './SchemaFieldList'
import SchemaFieldListGrouped from './SchemaFieldListGrouped'
import SchemaPreviewJson from './SchemaPreviewJson'
import SchemaPreviewForm from './SchemaPreviewForm'

function assignUids(fields) {
  if (!Array.isArray(fields)) {
    return []
  }
  return fields.map(f => {
    const field = { ...f }
    if (!field._uid) {
      field._uid = makeUid()
    }
    if (Array.isArray(field.schema)) {
      field.schema = assignUids(field.schema)
    }
    return field
  })
}

function assignFieldsetUids(fieldsets) {
  if (!Array.isArray(fieldsets)) {
    return []
  }
  return fieldsets.map(fs => {
    const fieldset = { ...fs }
    if (!fieldset._uid) {
      fieldset._uid = makeUid()
    }
    return fieldset
  })
}

function stripUids(fields) {
  if (!Array.isArray(fields)) {
    return []
  }
  return fields.map(f => {
    const field = { ...f }
    delete field._uid
    if (Array.isArray(field.schema)) {
      field.schema = stripUids(field.schema)
    }
    return field
  })
}

export default {
  components: {
    SchemaFieldsetEditor,
    SchemaNestedBreadcrumb,
    SchemaFieldList,
    SchemaFieldListGrouped,
    SchemaPreviewJson,
    SchemaPreviewForm,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      fields: [],
      fieldsets: [],
      nestedStack: [],
      viewMode: 'grouped',
      showFieldsets: false,
      showPreviewJson: false,
      showPreviewForm: false,
      showImport: false,
      importJson: '',
      allTypes: [],
      locales: [],
    }
  },
  computed: {
    isRoot() {
      return this.nestedStack.length === 0
    },
    contentTypes() {
      return this.allTypes.filter(t => t.kind !== 'fragment')
    },
    fragmentTypes() {
      return this.allTypes.filter(t => t.kind === 'fragment')
    },
    currentFields() {
      if (this.isRoot) {
        return this.fields
      }
      let target = this.fields
      for (const level of this.nestedStack) {
        const field = target[level.index]
        if (!field || !Array.isArray(field.schema)) {
          return []
        }
        target = field.schema
      }
      return target
    },
    outputSchema() {
      const cleanFields = stripUids(cloneDeep(this.fields))
      return buildSchemaJson(cleanFields, this.fieldsets)
    },
    validation() {
      return validateSchema(stripUids(this.fields), this.fieldsets)
    },
    displayErrors() {
      return this.isRoot ? this.validation.errors : []
    },
    displayWarnings() {
      return this.isRoot ? this.validation.warnings : []
    },
  },
  watch: {
    outputSchema: {
      handler(schema) {
        this.$emit('input', schema)
      },
      deep: true,
    },
    validation: {
      handler(v) {
        this.$emit('errors', v.errors)
      },
      immediate: true,
    },
  },
  created() {
    this.initFromValue()
    this.loadCmsTypes()
    this.loadLocales()
  },
  methods: {
    initFromValue() {
      const val = this.value || {}
      this.fieldsets = assignFieldsetUids(cloneDeep(val.fieldsets || []))
      this.fields = assignUids(cloneDeep(val.fields || []))
      if (this.fieldsets.length > 0) {
        this.viewMode = 'grouped'
      }
    },
    loadCmsTypes() {
      API.call('/cms/type/list.json').then(r => {
        this.allTypes = (r.data && r.data.items) || []
      }).catch(() => {
        this.allTypes = []
      })
    },
    loadLocales() {
      this.$store.dispatch('cms/main/loadLocales').then(locales => {
        this.locales = locales
      }).catch(() => {
        this.locales = []
      })
    },
    addField() {
      const fieldset = this.isRoot && this.fieldsets.length > 0 ? this.fieldsets[0].key : undefined
      const newField = createDefaultField(fieldset)
      newField._uid = makeUid()
      const fields = [...this.currentFields, newField]
      this.setCurrentFields(fields)
    },
    onFieldsUpdate(fields) {
      this.setCurrentFields(fields)
    },
    onFieldsetsUpdate(fieldsets) {
      this.fieldsets = fieldsets
    },
    setCurrentFields(fields) {
      if (this.isRoot) {
        this.fields = fields
        return
      }
      const rootFields = cloneDeep(this.fields)
      let target = rootFields
      for (let i = 0; i < this.nestedStack.length - 1; i++) {
        target = target[this.nestedStack[i].index].schema
      }
      const lastLevel = this.nestedStack[this.nestedStack.length - 1]
      target[lastLevel.index].schema = fields
      this.fields = rootFields
    },
    onEditNested(fieldIdx) {
      const fields = this.currentFields
      const field = fields[fieldIdx]
      if (!field) {
        return
      }
      if (!Array.isArray(field.schema)) {
        const updated = cloneDeep(fields)
        updated[fieldIdx].schema = []
        this.setCurrentFields(updated)
      }
      this.nestedStack.push({
        index: fieldIdx,
        key: field.key,
        label: field.label,
      })
    },
    navigateToLevel(levelIndex) {
      if (levelIndex === -1) {
        this.nestedStack = []
      } else {
        this.nestedStack = this.nestedStack.slice(0, levelIndex + 1)
      }
    },
    async doImport() {
      let parsed
      try {
        parsed = JSON.parse(this.importJson)
      } catch (e) {
        this.$showError('Invalid JSON: ' + e.message)
        return
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || !Array.isArray(parsed.fields)) {
        this.$showError('Not a valid schema: expected an object with a "fields" array')
        return
      }

      const hasCurrentSchema = this.fields.length > 0 || this.fieldsets.length > 0
      if (hasCurrentSchema) {
        const confirmed = await this.$root.$confirm(
          'Replace schema',
          'Importing will replace the current schema. Continue?'
        )
        if (!confirmed) {
          return
        }
      }

      this.fieldsets = assignFieldsetUids(cloneDeep(Array.isArray(parsed.fieldsets) ? parsed.fieldsets : []))
      this.fields = assignUids(cloneDeep(parsed.fields))
      this.nestedStack = []
      this.viewMode = this.fieldsets.length > 0 ? 'grouped' : 'flat'
      this.showImport = false
      this.importJson = ''
    },
  },
}
</script>

<style scoped>
.schema-constructor {
  min-height: 300px;
}
</style>

<style>
.schema-constructor .v-btn,
.schema-constructor .v-expansion-panel-header,
.schema-constructor .v-breadcrumbs-item:not(.v-breadcrumbs-item--disabled),
.schema-constructor .v-radio,
.schema-constructor .v-chip--removable .v-chip__close,
.schema-constructor .v-icon.clickable,
.schema-constructor .clickable {
  cursor: pointer !important;
}
</style>
